/**
 * AttachInvoiceModal — global modal that creates a PENDING (or instantly
 * PAID) invoice over an explicit set of UNPAID transactions.
 *
 * Reached via the Modal Manager pattern — every feature dispatches through
 * `openAttachInvoice(launch, opts)` → store flips → `AttachInvoiceModalRoot`
 * widget renders this component. The page that launched the modal supplies
 * the seed `txnIds`; the user can adjust the selection inside the modal.
 *
 * Field order (plan §4 + prototype `CreateInvoiceModal` lines 441–595):
 *   1. Title by kind + subtitle with selected-count.
 *   2. Type toggle [Чиқим тўлови][Кирим қабули] — flipping clears selection
 *      and seeds the new fetch (different `?type=` parameter).
 *   3. Payment-method cards (4-col CASH / TRANSFER / CARD / FX).
 *   4. Row of 3: account select · payment date · doc № — account auto-resets
 *      to the first option whenever method flips.
 *   5. Covered-txn checkbox table — scrollable, seeded from `txnIds`,
 *      sourced from `transactionsListOptions('?type=…&status=UNPAID', lang)`.
 *   6. Sticky totals strip: count · Σ amount (sign-coloured per kind).
 *   7. Note textarea.
 *   8. "Тўлов амалга оширилди (Кутилмоқда)" checkbox — controls `paid`
 *      flag on submit: OFF → invoice PENDING + txns INVOICED;
 *      ON → invoice PAID + txns SETTLED (same cascade as `markPaid`).
 *   9. Footer [Бекор қилиш][Инвойсни сақлаш], save disabled at 0 selected.
 *
 * Validation: per-field `zodRule()` adapters drive the AntD `Form.Item`
 * messages while the user types; the assembled payload re-parses through
 * `CreateInvoiceSchema` at submit so contract violations are caught before
 * the mutation fires (ACL boundary, never trust the form serializer alone).
 *
 * LINK mode (mode === 'LINK') is a forward-compatible API surface: the
 * launch payload accepts it for InvoiceDetail callers, but the UI here
 * renders a "in development" placeholder. Wiring the LINK flow is its own
 * MR alongside the InvoiceDetail page work.
 */

import {
  CreateInvoiceSchema,
  type CreateInvoice,
  type PaymentMethod,
  type Transaction,
  type TxnType,
} from '@/features/accounting/api/contracts'
import { useCreateInvoice } from '@/features/accounting/api/mutations'
import { transactionsListOptions } from '@/features/accounting/api/queries'
import { toISODate } from '@/features/accounting/lib/date'
import { formatAmount } from '@/features/accounting/lib/money'
import { CategoryTag, MoneyText, PartyCell } from '@/features/accounting/ui/components/common'
import type { AttachInvoiceKind, AttachInvoiceLaunchPayload } from '@/shared/stores'
import { Button } from '@/shared/ui/atoms/button'
import { Checkbox } from '@/shared/ui/atoms/checkbox'
import { Input } from '@/shared/ui/atoms/input'
import { RequiredMark } from '@/shared/ui/atoms/required-mark'
import { Textarea } from '@/shared/ui/atoms/textarea'
import { Datepicker } from '@/shared/ui/molecules/datepicker'
import { PopupModal } from '@/shared/ui/molecules/popup-modal'
import { SegmentedTabs, type SegmentedTabOption } from '@/shared/ui/molecules/segmented-tabs'
import { Toast } from '@/shared/ui/organisms/toast'
import { useQuery } from '@tanstack/react-query'
import { Form, Table as AntTable } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import type { TableRowSelection } from 'antd/es/table/interface'
import type { Dayjs } from 'dayjs'
import { CreditCard, Landmark, Tv, Wallet } from 'lucide-react'
import type { ComponentType, Key, ReactNode } from 'react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { dayjs } from '@/shared/lib/dayjs'

interface AttachInvoiceModalProps {
  open: boolean
  launch: AttachInvoiceLaunchPayload | null
  onClose: () => void
  onSaved?: () => void
}

// ─── Local constants ─────────────────────────────────────────────────────────

interface AccountOption {
  id: string
  name: string
}

/**
 * Accounts mirror `ACCOUNT_SEEDS` in `src/test/handlers/accounting.ts`. There
 * is no `/accounting/accounts` endpoint yet — when one ships, swap this
 * constant for a `useQuery(accountsListOptions)` hook.
 *
 * TODO[backend-swap]: replace with a fetched account list once the backend
 * exposes GET /v1/accounting/accounts. The 4 entries below match the MSW
 * seed verbatim so the create-invoice handler accepts them without 409.
 */
const ACCOUNTS_BY_METHOD: Record<PaymentMethod, readonly AccountOption[]> = {
  CASH: [{ id: 'acc-cash-1', name: 'Касса 1' }],
  TRANSFER: [{ id: 'acc-bank-1', name: 'Капиталбанк ...4127' }],
  CARD: [{ id: 'acc-card-1', name: 'UzCard ...8809' }],
  FX: [{ id: 'acc-fx-1', name: 'Asaka Bank ...USD' }],
}

const EMPTY_TXNS: readonly Transaction[] = []
const EMPTY_IDS: readonly string[] = []

type LucideIcon = ComponentType<{ size?: number | string; className?: string }>

const PAYMENT_METHODS: ReadonlyArray<{
  id: PaymentMethod
  labelKey: string
  hintKey: string
  Icon: LucideIcon
}> = [
  {
    id: 'CASH',
    labelKey: 'payment_method.CASH',
    hintKey: 'payment_method_hint.CASH',
    Icon: Wallet,
  },
  {
    id: 'TRANSFER',
    labelKey: 'payment_method.TRANSFER',
    hintKey: 'payment_method_hint.TRANSFER',
    Icon: Landmark,
  },
  {
    id: 'CARD',
    labelKey: 'payment_method.CARD',
    hintKey: 'payment_method_hint.CARD',
    Icon: CreditCard,
  },
  { id: 'FX', labelKey: 'payment_method.FX', hintKey: 'payment_method_hint.FX', Icon: Tv },
]

interface FormValues {
  type: TxnType
  paymentMethod: PaymentMethod
  accountId: string
  date: Dayjs
  docNo: string
  note: string
  paid: boolean
}

const buildDefaults = (kind: AttachInvoiceKind): FormValues => ({
  type: kind,
  paymentMethod: 'CASH',
  accountId: ACCOUNTS_BY_METHOD.CASH[0]!.id,
  date: dayjs(),
  docNo: '',
  note: '',
  paid: false,
})

// ─── Payment-method cards (local) ────────────────────────────────────────────

interface PaymentMethodCardsProps {
  value: PaymentMethod
  onChange: (id: PaymentMethod) => void
  translate: (key: string) => string
}

function PaymentMethodCards({ value, onChange, translate }: PaymentMethodCardsProps) {
  return (
    <div
      role='radiogroup'
      aria-label={translate('form.field_method')}
      className='grid grid-cols-2 gap-2 md:grid-cols-4'
    >
      {PAYMENT_METHODS.map((method) => {
        const isSelected = value === method.id
        const Icon = method.Icon
        return (
          <label
            key={method.id}
            className={[
              'flex cursor-pointer items-center gap-3 rounded-md border px-3 py-2.5 text-left transition-colors',
              'has-focus-visible:ring-2 has-focus-visible:ring-(--primary-btn-bg) has-focus-visible:ring-offset-1',
              isSelected
                ? 'border-(--primary-btn-bg) bg-[#1a1c3b0a]'
                : 'border-(--input-border) bg-white hover:bg-[#f6f8fa]',
            ].join(' ')}
          >
            <input
              type='radio'
              name='attach-invoice-method'
              value={method.id}
              checked={isSelected}
              onChange={() => onChange(method.id)}
              className='sr-only'
            />
            <Icon size={16} className='shrink-0 text-(--text-default)' aria-hidden='true' />
            <span className='flex min-w-0 flex-col leading-tight'>
              <span
                className={[
                  'truncate text-sm text-(--text-default)',
                  isSelected ? 'font-semibold' : 'font-medium',
                ].join(' ')}
              >
                {translate(method.labelKey)}
              </span>
              <span className='truncate text-xs text-(--text-secondary)'>
                {translate(method.hintKey)}
              </span>
            </span>
          </label>
        )
      })}
    </div>
  )
}

// ─── Type toggle icons ────────────────────────────────────────────────────────

function TrendingDownIcon() {
  return (
    <svg
      width='16'
      height='16'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden='true'
    >
      <polyline points='3 7 9 13 13 9 21 17' />
      <polyline points='14 17 21 17 21 10' />
    </svg>
  )
}

function TrendingUpIcon() {
  return (
    <svg
      width='16'
      height='16'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden='true'
    >
      <polyline points='3 17 9 11 13 15 21 7' />
      <polyline points='14 7 21 7 21 14' />
    </svg>
  )
}

// ─── Main modal ───────────────────────────────────────────────────────────────

export function AttachInvoiceModal({ open, launch, onClose, onSaved }: AttachInvoiceModalProps) {
  const { t, i18n } = useTranslation('accounting')
  const [form] = Form.useForm<FormValues>()
  const createMutation = useCreateInvoice()
  const isPending = createMutation.isPending

  const isLinkMode = launch?.mode === 'LINK'
  const launchKind: AttachInvoiceKind = launch?.kind ?? 'EXPENSE'

  // Modal-internal active kind. Mirrors the prototype's type toggle: when the
  // user flips inside the modal, selection clears (seeded ids belong to the
  // previous kind and would 409 on the server side).
  const [activeKind, setActiveKind] = useState<AttachInvoiceKind>(launchKind)
  const [selectedIds, setSelectedIds] = useState<readonly string[]>(
    () => launch?.txnIds ?? EMPTY_IDS,
  )

  // Reset state every time the modal opens with a fresh payload. Keeps the
  // form in sync with the launch context without leaking state across opens.
  useEffect(() => {
    if (!open || !launch) return
    const initialKind = launch.kind
    setActiveKind(initialKind)
    setSelectedIds(launch.txnIds ?? EMPTY_IDS)
    form.setFieldsValue(buildDefaults(initialKind))
  }, [open, launch, form])

  const watchedMethod =
    (Form.useWatch('paymentMethod', form) as PaymentMethod | undefined) ?? 'CASH'

  // Account list follows the method. Reset to the first option whenever the
  // method flips so the form never shows a stale account from another method.
  const accountOptions = ACCOUNTS_BY_METHOD[watchedMethod]
  useEffect(() => {
    if (!accountOptions[0]) return
    const currentAccountId = form.getFieldValue('accountId') as string | undefined
    if (!accountOptions.some((a) => a.id === currentAccountId)) {
      form.setFieldValue('accountId', accountOptions[0].id)
    }
  }, [accountOptions, form])

  const handleKindChange = (next: AttachInvoiceKind) => {
    setActiveKind(next)
    setSelectedIds(EMPTY_IDS)
    form.setFieldValue('type', next)
  }

  // ─── Covered-txn data ───────────────────────────────────────────────────────
  // Modal-internal source of truth — NOT the prototype's `TXNS` module global.
  // Filtered to UNPAID + the active kind; the page seed lives outside this
  // query so the user can toggle additional rows in/out before submitting.
  const txnsQuery = useQuery({
    ...transactionsListOptions(`?type=${activeKind}&status=UNPAID&size=200`, i18n.language),
    // Gate on `open`: this modal is mounted globally via AttachInvoiceModalRoot,
    // so without this the protected query fires on every route — including
    // /login while unauthenticated — causing a 401 → interceptor reload loop.
    enabled: open,
  })
  const coveredCandidates: readonly Transaction[] =
    txnsQuery.data?.content && txnsQuery.data.content.length > 0
      ? txnsQuery.data.content
      : EMPTY_TXNS

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds])
  const selectedRows = useMemo(
    () => coveredCandidates.filter((t) => selectedSet.has(t.id)),
    [coveredCandidates, selectedSet],
  )
  const selectedSum = selectedRows.reduce((s, r) => s + r.amount, 0)

  // ─── Form submission ────────────────────────────────────────────────────────

  const handleFinish = (values: FormValues) => {
    if (isLinkMode) {
      // Defensive — the UI should never let LINK reach submit, but guard
      // anyway so accidental future wiring fails loudly instead of silently.
      Toast({
        type: 'warning',
        message: t('page.pending_payments.attach_modal.link_mode_placeholder'),
      })
      return
    }
    if (selectedIds.length === 0) return
    const selectedAccount =
      accountOptions.find((a) => a.id === values.accountId) ?? accountOptions[0]
    if (!selectedAccount) {
      Toast({ type: 'error', message: t('errors.contract_violation') })
      return
    }
    const payload = {
      type: values.type,
      paymentMethod: values.paymentMethod,
      accountId: selectedAccount.id,
      accountName: selectedAccount.name,
      date: toISODate(values.date),
      payerName: values.type === 'EXPENSE' ? 'Glasspro MChJ' : '',
      payeeName: values.type === 'EXPENSE' ? '' : 'Glasspro MChJ',
      currency: 'UZS' as const,
      docNo: values.docNo.trim(),
      note: values.note,
      txnIds: [...selectedIds],
      paid: values.paid,
    }
    const parsed = CreateInvoiceSchema.safeParse(payload)
    if (!parsed.success) {
      Toast({ type: 'error', message: t('errors.contract_violation') })
      return
    }
    const body = parsed.data as CreateInvoice
    createMutation.mutate(body, {
      onSuccess: () => {
        Toast({
          type: 'success',
          message: t('page.pending_payments.attach_modal.save_success'),
        })
        onSaved?.()
        onClose()
      },
    })
  }

  // ─── Header ─────────────────────────────────────────────────────────────────

  const title =
    activeKind === 'EXPENSE'
      ? t('page.pending_payments.attach_modal.title_expense')
      : t('page.pending_payments.attach_modal.title_income')

  const titleNode = (
    <div className='flex flex-col gap-1'>
      <span className='text-base font-semibold text-(--text-default)'>{title}</span>
      <span className='text-sm font-normal text-(--text-secondary)'>
        {t('page.pending_payments.attach_modal.subtitle', { count: selectedIds.length })}
      </span>
    </div>
  )

  // ─── Type toggle ────────────────────────────────────────────────────────────

  const typeTabOptions: SegmentedTabOption<AttachInvoiceKind>[] = useMemo(
    () => [
      {
        value: 'EXPENSE',
        label: t('page.pending_payments.attach_modal.type_expense'),
        icon: <TrendingDownIcon />,
        iconClassName: 'text-(--text-error)',
      },
      {
        value: 'INCOME',
        label: t('page.pending_payments.attach_modal.type_income'),
        icon: <TrendingUpIcon />,
        iconClassName: 'text-(--text-success)',
      },
    ],
    [t],
  )

  // ─── Covered-txn table ──────────────────────────────────────────────────────

  const txnColumns: ColumnsType<Transaction> = useMemo(
    () => [
      {
        title: t('column.number'),
        dataIndex: 'id',
        key: 'id',
        width: 130,
        render: (id: string) => (
          <span className='font-mono text-xs text-(--text-default)'>{id}</span>
        ),
      },
      {
        title: t('column.date'),
        dataIndex: 'date',
        key: 'date',
        width: 110,
        render: (date: string) => <span className='text-sm text-(--text-secondary)'>{date}</span>,
      },
      {
        title: t('column.category'),
        dataIndex: 'categoryId',
        key: 'categoryId',
        render: (_unused, row) => <CategoryTag categoryId={row.categoryId} />,
      },
      {
        title: t('column.party'),
        dataIndex: 'partyName',
        key: 'partyName',
        render: (_unused, row) => (
          <PartyCell name={row.partyName} sub={row.note ? row.note : undefined} />
        ),
      },
      {
        title: t('column.amount'),
        dataIndex: 'amount',
        key: 'amount',
        align: 'right',
        width: 160,
        render: (_unused, row) => (
          <div className='flex justify-end'>
            <MoneyText
              amount={row.amount}
              sign={row.type === 'INCOME' ? 'force-positive' : 'force-negative'}
              colored
            />
          </div>
        ),
      },
    ],
    [t],
  )

  const txnRowSelection: TableRowSelection<Transaction> = {
    selectedRowKeys: [...selectedIds] as Key[],
    columnWidth: 50,
    onChange: (keys) => setSelectedIds(keys.map(String)),
  }

  // ─── Render ─────────────────────────────────────────────────────────────────

  if (isLinkMode) {
    return (
      <PopupModal isModalOpen={open} onCancel={onClose} width='540px' title={titleNode}>
        <div className='flex flex-col items-center gap-4 px-8 py-12 text-center'>
          <p className='m-0 text-sm text-(--text-secondary)'>
            {t('page.pending_payments.attach_modal.link_mode_placeholder')}
          </p>
          <Button onClick={onClose}>{t('actions.cancel')}</Button>
        </div>
      </PopupModal>
    )
  }

  const requiredMessage = t('errors.required_field')
  const submitDisabled = isPending || selectedIds.length === 0

  const totalsCountLabel = t('page.pending_payments.attach_modal.totals_summary', {
    count: selectedIds.length,
  })

  // Sticky strip tone: INCOME → green positive, EXPENSE → red negative — mirrors
  // the table's per-row sign convention so the total reads the same way.
  const totalsToneClass = activeKind === 'INCOME' ? 'text-(--text-success)' : 'text-(--text-error)'

  return (
    <PopupModal isModalOpen={open} onCancel={onClose} width='920px' title={titleNode}>
      <Form
        form={form}
        layout='vertical'
        onFinish={handleFinish}
        initialValues={buildDefaults(launchKind)}
        className='flex flex-col gap-4'
        requiredMark={(labelNode, info) => (
          <>
            {labelNode}
            {info.required ? <RequiredMark /> : null}
          </>
        )}
      >
        <Form.Item name='type' label={t('form.field_type')} required>
          <SegmentedTabs<AttachInvoiceKind>
            variant='pill'
            options={typeTabOptions}
            value={activeKind}
            onChange={handleKindChange}
            aria-label={t('form.field_type')}
          />
        </Form.Item>

        <Form.Item name='paymentMethod' label={t('form.field_method')} required>
          <PaymentMethodCards value={watchedMethod} onChange={() => {}} translate={t} />
        </Form.Item>

        <div className='grid grid-cols-1 gap-4 md:grid-cols-3'>
          <Form.Item
            name='accountId'
            label={t('form.field_account')}
            rules={[{ required: true, message: requiredMessage }]}
          >
            <AccountSelect options={accountOptions} translate={t} />
          </Form.Item>
          <Form.Item
            name='date'
            label={t('form.field_date')}
            rules={[{ required: true, message: requiredMessage }]}
          >
            <Datepicker
              format='DD MMM, YYYY'
              aria-label={t('form.field_date')}
              onChange={() => {}}
            />
          </Form.Item>
          <Form.Item name='docNo' label={t('form.field_doc_no')}>
            <Input
              aria-label={t('form.field_doc_no')}
              placeholder={t('page.pending_payments.attach_modal.field_doc_no_placeholder')}
              maxLength={40}
            />
          </Form.Item>
        </div>

        <div className='flex flex-col gap-2'>
          <span className='text-sm font-medium text-(--text-default)'>
            {t('page.pending_payments.attach_modal.covered_txns_label')}
          </span>
          {coveredCandidates.length === 0 ? (
            <div
              role='status'
              className='flex items-center justify-center rounded-md border border-(--input-border) bg-white p-6 text-sm text-(--text-secondary)'
            >
              {t('page.pending_payments.attach_modal.covered_txns_empty')}
            </div>
          ) : (
            <div className='max-h-[220px] overflow-auto rounded-md border border-(--input-border)'>
              <AntTable<Transaction>
                columns={txnColumns}
                dataSource={coveredCandidates as Transaction[]}
                rowSelection={txnRowSelection}
                rowKey='id'
                pagination={false}
                size='small'
              />
            </div>
          )}
          <StickyTotals
            countLabel={totalsCountLabel}
            sumNode={
              <span className={`tabular-nums font-semibold ${totalsToneClass}`}>
                {formatAmount(selectedSum)} UZS
              </span>
            }
          />
        </div>

        <Form.Item name='note' label={t('form.field_note')}>
          <Textarea
            aria-label={t('form.field_note')}
            placeholder={t('form.field_note_placeholder')}
            rows={2}
            maxLength={500}
          />
        </Form.Item>

        <Form.Item name='paid' valuePropName='checked' className='m-0'>
          <Checkbox aria-label={t('form.field_paid_done')}>{t('form.field_paid_done')}</Checkbox>
        </Form.Item>

        <div className='flex items-center justify-end gap-2 pt-2'>
          <Button onClick={onClose} disabled={isPending}>
            {t('actions.cancel')}
          </Button>
          <Button type='primary' htmlType='submit' loading={isPending} disabled={submitDisabled}>
            {t('page.pending_payments.attach_modal.submit')}
          </Button>
        </div>
      </Form>
    </PopupModal>
  )
}

// ─── Sticky totals strip ──────────────────────────────────────────────────────

interface StickyTotalsProps {
  countLabel: string
  sumNode: ReactNode
}

function StickyTotals({ countLabel, sumNode }: StickyTotalsProps) {
  return (
    <div className='flex items-center justify-between rounded-md border border-(--input-border) bg-[#f6f8fa] px-3 py-2 text-sm'>
      <span className='text-(--text-secondary)'>{countLabel}</span>
      {sumNode}
    </div>
  )
}

// ─── Account select wrapper ──────────────────────────────────────────────────
// AntD Form.Item clones its child with `value` + `onChange` injected, so this
// wrapper stays a thin presentational layer. Using a native select keeps the
// modal free of the shared `CustomSelect`'s dropdown-width state without
// losing keyboard accessibility.

interface AccountSelectProps {
  value?: string
  onChange?: (id: string) => void
  options: readonly AccountOption[]
  translate: (key: string) => string
}

function AccountSelect({ value, onChange, options, translate }: AccountSelectProps) {
  return (
    <select
      aria-label={translate('form.field_account')}
      value={value ?? ''}
      onChange={(e) => onChange?.(e.target.value)}
      className='h-10 w-full rounded-md border border-(--input-border) bg-white px-3 text-sm text-(--text-default) focus:border-(--primary-btn-bg) focus:outline-none'
    >
      {options.length === 0 ? (
        <option value=''>
          {translate('page.pending_payments.attach_modal.field_account_placeholder')}
        </option>
      ) : (
        options.map((opt) => (
          <option key={opt.id} value={opt.id}>
            {opt.name}
          </option>
        ))
      )}
    </select>
  )
}
